import { i18n } from "@lingui/core";
import { plural, t } from "@lingui/core/macro";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import type {
  GetCustomerSubscriptionInvoice,
  SubscriptionDurationUnit,
} from "@/server-types";

dayjs.extend(utc);

export type SubscriptionInvoice =
  GetCustomerSubscriptionInvoice["response"]["invoices"][number];

/** One payer's part of a period: the member's, or a funding company's. */
export type SubscriptionInvoiceShare = SubscriptionInvoice["shares"][number];

/** The server puts these fields on a period and on each of its shares alike, so the cells render either. */
export type DocumentBearing = Pick<
  SubscriptionInvoice,
  | "spaceInvoicesDocumentId"
  | "type"
  | "number"
  | "canceled"
  | "payments"
  | "date"
  | "documentDateFormatted"
  | "estimateNotificationStatus"
>;

/** A co-funded period is billed to each payer separately. */
export function isSplitPeriod(invoice: SubscriptionInvoice): boolean {
  return invoice.shares.length > 1;
}

/** A flat row reads its sole share, not the period: when a company funds 100%, that share is the company's. */
export function getPeriodDocument(
  invoice: SubscriptionInvoice,
): DocumentBearing {
  return invoice.shares[0] ?? invoice;
}

export function getShareCompanyName(share: SubscriptionInvoiceShare): string {
  return share.company?.companyName ?? t`Stranka`;
}

export type PeriodDocument = {
  documentId: string;
  payerName: string;
  number: string | null;
  type: "estimate" | "invoice" | null;
  canceled: boolean;
  memberCount: number;
};

/** A document that bills several members is cancelled or unlinked for every one of them at once. */
export function isDocumentSharedWithOtherMembers(document: {
  memberCount: number;
}): boolean {
  return document.memberCount > 1;
}

export function formatMemberCount(count: number): string {
  return plural(count, {
    one: "# člana",
    two: "# člana",
    few: "# člane",
    other: "# članov",
  });
}

function toDocumentType(
  type: DocumentBearing["type"],
): "estimate" | "invoice" | null {
  return type === "estimate" || type === "invoice" ? type : null;
}

/** One per payer issued a document: deleting the period acts on all of them, not only the member's. */
export function getPeriodDocuments(
  invoice: SubscriptionInvoice,
): PeriodDocument[] {
  if (invoice.shares.length === 0) {
    return invoice.spaceInvoicesDocumentId
      ? [
          {
            documentId: invoice.spaceInvoicesDocumentId,
            payerName: t`Stranka`,
            number: invoice.number ?? null,
            type: toDocumentType(invoice.type),
            canceled: !!invoice.canceled,
            memberCount: 1,
          },
        ]
      : [];
  }

  return invoice.shares.flatMap((share) =>
    share.spaceInvoicesDocumentId
      ? [
          {
            documentId: share.spaceInvoicesDocumentId,
            payerName: getShareCompanyName(share),
            number: share.number ?? null,
            type: toDocumentType(share.type),
            canceled: !!share.canceled,
            memberCount: share.documentMemberCount,
          },
        ]
      : [],
  );
}

/** A co-funded period lists a sub-row per payer under its summary row; a single-payer period stays flat. */
export type SubscriptionInvoiceRow =
  | { kind: "period"; key: string; invoice: SubscriptionInvoice }
  | {
      kind: "share";
      key: string;
      invoice: SubscriptionInvoice;
      share: SubscriptionInvoiceShare;
      isLast: boolean;
    };

export function buildSubscriptionInvoiceRows(
  invoices: SubscriptionInvoice[],
  collapsedPeriodIds?: ReadonlySet<number>,
): SubscriptionInvoiceRow[] {
  const rows: SubscriptionInvoiceRow[] = [];

  for (const invoice of invoices) {
    rows.push({ kind: "period", key: `period-${invoice.id}`, invoice });

    if (!isSplitPeriod(invoice) || collapsedPeriodIds?.has(invoice.id)) {
      continue;
    }
    invoice.shares.forEach((share, index) => {
      rows.push({
        kind: "share",
        key: `share-${share.id}`,
        invoice,
        share,
        isLast: index === invoice.shares.length - 1,
      });
    });
  }

  return rows;
}

export function getPaymentTypeLabel(paymentType: string) {
  switch (paymentType) {
    case "bank":
      return t`Bančno nakazilo`;
    case "online":
      return t`Spletno plačilo`;
    case "cash":
      return t`Gotovina`;
    case "card":
      return t`Kartica`;
    case "coupon":
      return t`Darilni bon`;
    default:
      return paymentType;
  }
}

export function getInvoiceStatus(invoice: SubscriptionInvoice) {
  if (invoice.paidAt) {
    return {
      value: "paid",
      text: t`Plačano`,
    };
  }
  // `paidAt` is only set once every payer has settled, so a paid share alone means partly paid.
  if (invoice.shares.some((share) => share.paidAt)) {
    return {
      value: "partially_paid",
      text: t`Delno plačano`,
    };
  }
  return {
    value: "unpaid",
    text: t`Neplačano`,
  };
}

export function getShareStatus(share: SubscriptionInvoiceShare) {
  return share.paidAt
    ? { value: "paid", text: t`Plačano` }
    : { value: "unpaid", text: t`Neplačano` };
}

export function formatInvoicePeriodDate(date: Date | string): string {
  return new Intl.DateTimeFormat(i18n.locale, {
    timeZone: "UTC",
  }).format(new Date(date));
}

function formatInvoicePeriodDateTime(date: Date | string): string {
  return new Intl.DateTimeFormat(i18n.locale, {
    timeZone: "UTC",
    day: "numeric",
    month: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(new Date(date));
}

export function formatPeriodRange(
  periodStart: Date | string,
  periodEnd: Date | string,
): string {
  return `${formatInvoicePeriodDateTime(periodStart)} – ${formatInvoicePeriodDateTime(periodEnd)}`;
}

function addSubscriptionDuration(
  date: Date | string,
  unit: SubscriptionDurationUnit,
  count: number,
): Date {
  const base = dayjs.utc(date);
  switch (unit) {
    case "day":
      return base.add(count, "day").toDate();
    case "week":
      return base.add(count * 7, "day").toDate();
    case "month":
      return base.add(count, "month").toDate();
    case "year":
      return base.add(count, "year").toDate();
  }
}

function getSubscriptionPeriodEnd(
  periodStart: Date | string,
  unit: SubscriptionDurationUnit,
  count: number,
): Date {
  return dayjs
    .utc(addSubscriptionDuration(periodStart, unit, count))
    .subtract(1, "day")
    .endOf("day")
    .toDate();
}

/** Dates for a period appended immediately after the newest existing period,
 * matching the server's contiguous period math (00:00 → 23:59 UTC, no gap). */
export function getNextPeriodDates(
  newestPeriodEnd: Date | string,
  unit: SubscriptionDurationUnit,
  durationValue: number,
): { periodStart: Date; periodEnd: Date } {
  const periodStart = dayjs
    .utc(newestPeriodEnd)
    .add(1, "day")
    .startOf("day")
    .toDate();
  return {
    periodStart,
    periodEnd: getSubscriptionPeriodEnd(periodStart, unit, durationValue),
  };
}

type InvoicePeriod = {
  id: number;
  periodStart: Date | string;
  periodEnd: Date | string;
};

export type FuturePeriodCorrection = {
  invoiceId: number;
  currentPeriodStart: Date;
  currentPeriodEnd: Date;
  periodStart: Date;
  periodEnd: Date;
};

const startOfDayValue = (date: Date | string) =>
  dayjs.utc(date).startOf("day").valueOf();

/** Periods that would move if the edited period were saved with the
 * "correct future periods" switch on. Mirrors the server's
 * `computeFollowingInvoicePeriods` — including how it picks the anchor period —
 * so the preview matches what saving actually writes. Keep the two in sync. */
export function computeFutureInvoicePeriodCorrections({
  invoices,
  editedInvoiceId,
  periodStart,
  periodEnd,
  unit,
  durationValue,
}: {
  invoices: InvoicePeriod[];
  editedInvoiceId: number;
  periodStart: Date;
  periodEnd: Date;
  unit: SubscriptionDurationUnit;
  durationValue: number;
}): FuturePeriodCorrection[] {
  const simulated = invoices
    .map((invoice) =>
      invoice.id === editedInvoiceId
        ? { ...invoice, periodStart, periodEnd }
        : invoice,
    )
    .sort(
      (a, b) =>
        startOfDayValue(a.periodStart) - startOfDayValue(b.periodStart) ||
        a.id - b.id,
    );

  const anchor = startOfDayValue(periodStart);
  const anchorIndex = (() => {
    const byStart = simulated.findIndex(
      (invoice) => startOfDayValue(invoice.periodStart) === anchor,
    );
    if (byStart >= 0) return byStart;
    return simulated.reduce(
      (last, invoice, index) =>
        startOfDayValue(invoice.periodStart) <= anchor &&
        startOfDayValue(invoice.periodEnd) >= anchor
          ? index
          : last,
      -1,
    );
  })();

  const startIndex =
    anchorIndex >= 0
      ? anchorIndex + 1
      : simulated.findIndex(
          (invoice) => startOfDayValue(invoice.periodStart) >= anchor,
        );

  if (anchorIndex < 0 && startIndex < 0) return [];

  const initialPreviousPeriodEnd =
    anchorIndex >= 0
      ? simulated[anchorIndex]!.periodEnd
      : dayjs.utc(periodStart).subtract(1, "day").endOf("day").toDate();

  const corrections: FuturePeriodCorrection[] = [];
  let previousPeriodEnd: Date | string = initialPreviousPeriodEnd;

  for (const invoice of simulated.slice(startIndex)) {
    const next = getNextPeriodDates(previousPeriodEnd, unit, durationValue);
    const currentPeriodStart = new Date(invoice.periodStart);
    const currentPeriodEnd = new Date(invoice.periodEnd);

    if (
      currentPeriodStart.getTime() !== next.periodStart.getTime() ||
      currentPeriodEnd.getTime() !== next.periodEnd.getTime()
    ) {
      corrections.push({
        invoiceId: invoice.id,
        currentPeriodStart,
        currentPeriodEnd,
        ...next,
      });
    }

    previousPeriodEnd = next.periodEnd;
  }

  return corrections;
}

/** Dates for a period inserted immediately before the oldest existing period,
 * so its end abuts the oldest period's start (00:00 → 23:59 UTC, no gap). */
export function getPreviousPeriodDates(
  oldestPeriodStart: Date | string,
  unit: SubscriptionDurationUnit,
  durationValue: number,
): { periodStart: Date; periodEnd: Date } {
  const normalizedOldestStart = dayjs.utc(oldestPeriodStart).startOf("day");
  const periodStart = addSubscriptionDuration(
    normalizedOldestStart.toDate(),
    unit,
    -durationValue,
  );
  return {
    periodStart,
    periodEnd: normalizedOldestStart.subtract(1, "day").endOf("day").toDate(),
  };
}

export function findIssuedEstimateId(
  documents: { documentId: string; shareIds: number[] }[],
  shareId: number,
): string | null {
  return (
    documents.find((document) => document.shareIds.includes(shareId))
      ?.documentId ?? null
  );
}

/** The POS page that turns the estimate into an invoice, then returns to `redirectUrl`. */
export function getEstimateConversionUrl(
  estimateId: string,
  redirectUrl: string,
): string {
  const params = new URLSearchParams({ fromEstimate: estimateId, redirectUrl });
  return `/dashboard/tax-register/invoice/new?${params.toString()}`;
}

type ExitChargeSale = { customerSubscriptionId: number; commitmentId: number };

export const EXIT_CHARGE_SALE_PARAMS = {
  customerSubscriptionId: "exitChargeSubscriptionId",
  commitmentId: "exitChargeCommitmentId",
} as const;

/** The POS with a recorded exit charge in the cart, then back to `redirectUrl`. */
export function getExitChargeSaleUrl(
  sale: ExitChargeSale,
  redirectUrl: string,
): string {
  const params = new URLSearchParams({
    [EXIT_CHARGE_SALE_PARAMS.customerSubscriptionId]: String(
      sale.customerSubscriptionId,
    ),
    [EXIT_CHARGE_SALE_PARAMS.commitmentId]: String(sale.commitmentId),
    redirectUrl,
  });
  return `/dashboard/tax-register/invoice/new?${params.toString()}`;
}

export function readExitChargeSale(
  searchParams: URLSearchParams,
): ExitChargeSale | null {
  const customerSubscriptionId = Number(
    searchParams.get(EXIT_CHARGE_SALE_PARAMS.customerSubscriptionId),
  );
  const commitmentId = Number(
    searchParams.get(EXIT_CHARGE_SALE_PARAMS.commitmentId),
  );
  if (
    !Number.isInteger(customerSubscriptionId) ||
    !Number.isInteger(commitmentId) ||
    customerSubscriptionId <= 0 ||
    commitmentId <= 0
  ) {
    return null;
  }
  return { customerSubscriptionId, commitmentId };
}

export function getPeriodEstimateActions(
  invoice: Pick<SubscriptionInvoice, "spaceInvoicesDocumentId" | "type">,
  isTaxRegisterIntegrationEnabled: boolean,
) {
  const hasDocument = !!invoice.spaceInvoicesDocumentId;

  return {
    canIssueEstimate: isTaxRegisterIntegrationEnabled && !hasDocument,
    canIssueInvoice: isTaxRegisterIntegrationEnabled && !hasDocument,
    canLinkDocument: isTaxRegisterIntegrationEnabled && !hasDocument,
    canRemoveEstimate:
      isTaxRegisterIntegrationEnabled &&
      hasDocument &&
      invoice.type === "estimate",
  };
}
